import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CreateWorkflowRequest,
  UpdateWorkflowOperationItem,
  UpdateWorkflowRequest,
  Workflow,
  WorkflowListItem,
  WorkflowOperation,
  WorkflowOperationPrerequisite,
} from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  getAll,
  getOne,
  guidRegex,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { WorkflowsService } from './workflows.service';

type WorkflowsState = {
  workflows: WorkflowListItem[];
  isLoading: boolean;
  selectedWorkflow: Workflow | null;
  isSelectedWorkflowLoading: boolean;
  isWorkflowCreateLoading: boolean;
  isWorkflowOperationsLoading: boolean;
  isWorkflowOperationAddLoading: boolean;
  isWorkflowOperationPrerequisitesLoading: boolean;
  selectedOperation: WorkflowOperation | null;
};

const initialState: WorkflowsState = {
  workflows: [],
  isLoading: false,
  selectedWorkflow: null,
  isSelectedWorkflowLoading: false,
  isWorkflowCreateLoading: false,
  isWorkflowOperationsLoading: false,
  isWorkflowOperationAddLoading: false,
  isWorkflowOperationPrerequisitesLoading: false,
  selectedOperation: null,
};

export const workflowsDictionary: StateDictionary<WorkflowListItem> = {
  entityList: 'workflows',
  listLoading: 'isLoading',
  createLoading: 'isWorkflowCreateLoading',
  selectedEntity: 'selectedWorkflow',
  selectedEntityLoading: 'isSelectedWorkflowLoading',
  entityId: 'id',
  sortFn: (a: WorkflowListItem, b: WorkflowListItem) => a.name.localeCompare(b.name),
};

export const WorkflowsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('workflows-store'),
  withComputed(store => ({
    workflowOperations: computed<WorkflowOperation[]>(() => store.selectedWorkflow()?.workflowOperations ?? []),
  })),
  withMethods((store, workflowsService = inject(WorkflowsService), router = inject(Router)) => {
    createStateResetHandler<WorkflowsState>('/job-orchestrator/workflows')(store, initialState);
    createCustomStateSetOnUrlHandler<WorkflowsState>('/job-orchestrator/workflows', {
      selectedWorkflow: null,
      selectedOperation: null,
    })(store);
    createCustomStateSetOnRegexHandler<WorkflowsState>(new RegExp(`^/job-orchestrator/workflows/(${guidRegex})/operations$`))(store, {
      selectedOperation: null,
    });

    const repositoryMethods = {
      getAll: getAll<WorkflowListItem>({
        store,
        service: workflowsService,
        dictionary: workflowsDictionary,
      }),

      getOne: getOne<Workflow>({
        store,
        service: workflowsService,
        dictionary: workflowsDictionary,
        onSuccessCallback: (workflow: Workflow) => {
          const selectedOperationId = store.selectedOperation()?.id;
          if (selectedOperationId) {
            patchState(store, {
              selectedOperation: workflow.workflowOperations.find(o => o.id === selectedOperationId) ?? null,
            });
          }
        },
      }),

      create: addEntity<Workflow, CreateWorkflowRequest>({
        store,
        service: workflowsService,
        dictionary: workflowsDictionary,
        onSuccessCallback: (newWorkflow: Workflow) => {
          patchState(store, { selectedWorkflow: newWorkflow });
          router.navigate(['job-orchestrator', 'workflows', newWorkflow.id, 'details']);
        },
      }),

      update: updateEntity<Workflow, UpdateWorkflowRequest>({
        store,
        service: workflowsService,
        dictionary: workflowsDictionary,
        onSuccessCallback: (updatedWorkflow: Workflow) => {
          patchState(store, { selectedWorkflow: updatedWorkflow });
        },
      }),

      delete: deleteEntity<Workflow>({
        store,
        service: workflowsService,
        dictionary: workflowsDictionary,
        onSuccessCallback: () => {
          patchState(store, { selectedOperation: null });
          router.navigate(['job-orchestrator', 'workflows']);
        },
      }),

      addOperations: rxMethod<{ workflowId: string; items: UpdateWorkflowOperationItem[] }>(
        pipe(
          tap(() =>
            patchState(store, {
              isWorkflowOperationsLoading: true,
              isSelectedWorkflowLoading: true,
              isWorkflowOperationAddLoading: true,
            }),
          ),
          switchMap(({ workflowId, items }) =>
            workflowsService.updateOperations(workflowId, items).pipe(
              tap((workflow: Workflow) => {
                patchState(store, {
                  selectedWorkflow: workflow,
                  isWorkflowOperationsLoading: false,
                  isSelectedWorkflowLoading: false,
                  isWorkflowOperationAddLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, {
                  isWorkflowOperationsLoading: false,
                  isSelectedWorkflowLoading: false,
                  isWorkflowOperationAddLoading: false,
                });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      updateOperation: rxMethod<{ id: string; dto: { lineNo: number } }>(
        pipe(
          tap(() => patchState(store, { isSelectedWorkflowLoading: true })),
          switchMap(({ id, dto }) => {
            const workflowId = store.selectedWorkflow()!.id;
            const currentOps = store.workflowOperations();
            const updated: UpdateWorkflowOperationItem[] = currentOps.map(o => ({
              id: o.id,
              operationId: o.operationId,
              lineNo: o.id === id ? dto.lineNo : o.lineNo,
            }));
            return workflowsService.updateOperations(workflowId, updated).pipe(
              tap((workflow: Workflow) => {
                patchState(store, {
                  selectedWorkflow: workflow,
                  selectedOperation: workflow.workflowOperations.find(o => o.id === id) ?? null,
                  isSelectedWorkflowLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isSelectedWorkflowLoading: false });
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      deleteOperation: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSelectedWorkflowLoading: true })),
          switchMap(id => {
            const workflowId = store.selectedWorkflow()!.id;
            const currentOps: UpdateWorkflowOperationItem[] = store
              .workflowOperations()
              .filter(o => o.id !== id)
              .map(o => ({ id: o.id, operationId: o.operationId, lineNo: o.lineNo }));
            return workflowsService.updateOperations(workflowId, currentOps).pipe(
              tap((workflow: Workflow) => {
                patchState(store, {
                  selectedWorkflow: workflow,
                  isSelectedWorkflowLoading: false,
                });
                router.navigate(['job-orchestrator', 'workflows', workflowId, 'operations']);
              }),
              catchError(() => {
                patchState(store, { isSelectedWorkflowLoading: false });
                return EMPTY;
              }),
            );
          }),
        ),
      ),

      updatePrerequisites: rxMethod<{ workflowId: string; workflowOperationId: string; prerequisites: WorkflowOperationPrerequisite[] }>(
        pipe(
          tap(() => patchState(store, { isWorkflowOperationPrerequisitesLoading: true, isSelectedWorkflowLoading: true })),
          switchMap(({ workflowId, workflowOperationId, prerequisites }) => {
            const previousWorkflow = store.selectedWorkflow();
            const previousOperation = store.selectedOperation();
            if (previousWorkflow) {
              patchState(store, {
                selectedWorkflow: {
                  ...previousWorkflow,
                  workflowOperations: previousWorkflow.workflowOperations.map(o =>
                    o.id === workflowOperationId ? { ...o, prerequisites } : o,
                  ),
                },
              });
            }

            return workflowsService.updatePrerequisites(workflowId, workflowOperationId, prerequisites).pipe(
              tap((workflow: Workflow) => {
                patchState(store, {
                  selectedWorkflow: workflow,
                  selectedOperation: workflow.workflowOperations.find(o => o.id === workflowOperationId) ?? null,
                  isWorkflowOperationPrerequisitesLoading: false,
                  isSelectedWorkflowLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, {
                  selectedWorkflow: previousWorkflow,
                  selectedOperation: previousOperation,
                  isWorkflowOperationPrerequisitesLoading: false,
                  isSelectedWorkflowLoading: false,
                });
                return EMPTY;
              }),
            );
          }),
        ),
      ),
    };

    const navigationActions = {
      goToWorkflowActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['job-orchestrator', 'workflows', id]);
        return result ?? false;
      },

      selectOperation: (id: string): void => {
        const op = store.workflowOperations()!.find(o => o.id === id) ?? null;
        patchState(store, { selectedOperation: op });
      },

      goToOperationActions: async (workflowId: string, operationId: string): Promise<boolean> => {
        const result = await router.navigate(['job-orchestrator', 'workflows', workflowId, 'operations', operationId]);
        if (result) {
          navigationActions.selectOperation(operationId);
          return result;
        }
        return result ?? false;
      },

      goToOperationList: (workflowId: string): void => {
        patchState(store, { selectedOperation: null });
        router.navigate(['job-orchestrator', 'workflows', workflowId, 'operations']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface WorkflowsStoreType {
  readonly workflows: Signal<WorkflowListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedWorkflow: Signal<Workflow | null>;
  readonly isSelectedWorkflowLoading: Signal<boolean>;
  readonly workflowOperations: Signal<WorkflowOperation[]>;
  readonly isWorkflowOperationsLoading: Signal<boolean>;
  readonly isWorkflowOperationAddLoading: Signal<boolean>;
  readonly isWorkflowOperationPrerequisitesLoading: Signal<boolean>;
  readonly isWorkflowCreateLoading: Signal<boolean>;
  readonly selectedOperation: Signal<WorkflowOperation | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly create: (request: CreateWorkflowRequest) => void;
  readonly update: (params: { id: string; dto: UpdateWorkflowRequest }) => void;
  readonly delete: (id: string) => void;
  readonly addOperations: (params: { workflowId: string; items: UpdateWorkflowOperationItem[] }) => void;
  readonly goToWorkflowActions: (id: string) => Promise<boolean>;
  readonly selectOperation: (id: string) => void;
  readonly updateOperation: (params: { id: string; dto: { lineNo: number } }) => void;
  readonly deleteOperation: (operationId: string) => void;
  readonly updatePrerequisites: (params: {
    workflowId: string;
    workflowOperationId: string;
    prerequisites: WorkflowOperationPrerequisite[];
  }) => void;
  readonly goToOperationActions: (workflowId: string, operationId: string) => Promise<boolean>;
  readonly goToOperationList: (workflowId: string) => void;
}
